import { useEffect, useState } from 'react';
import { formatDate } from '../utils/dateUtils';
import axios from 'axios';
import BottomNav from '../components/BottomNav';
import { linkify } from '../utils/linkify';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import useAutoRefresh from '../utils/refresh';

export default function NextShift() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [nextShift, setNextShift] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCheckinModal, setShowCheckinModal] = useState(false);
  const [checkinDone, setCheckinDone] = useState(false);
  const [checkinLoading, setCheckinLoading] = useState(false);
  // Refetch when the app returns to the foreground, when BottomNav's poll
  // sees a counter rise, or when the active tab is tapped. See utils/refresh.js.
  const refreshKey = useAutoRefresh();

  useEffect(() => {
    axios.get('/api/staff/schedule')
      .then(r => {
        const now = new Date();
        const next = r.data.find(s => {
          try {
            const start = s.time_start || '00:00';
            const t = s.time_end && s.time_end.trim() !== '' ? s.time_end : '23:59';
            // Session W (W4): explicit +02:00 — the stored time is SAST and the
            // server treats it as such. Parsing it in the device's timezone made
            // "which shift is next" depend on the phone's clock settings.
            const end = new Date(`${s.date}T${t}:00+02:00`);
            const [sh, sm] = start.split(':').map(Number);
            const [eh, em] = t.split(':').map(Number);
            if ((eh * 60 + em) < (sh * 60 + sm)) end.setDate(end.getDate() + 1); // overnight — ends next day
            return end >= now;
          } catch { return true; }
        });
        setNextShift(next || null);
        if (next?.checked_in) setCheckinDone(true);
      })
      .finally(() => setLoading(false));
    // ⚠️ `loading` is only ever set false, never back to true, so a refetch
    // leaves the current shift on screen and swaps it when the data lands.
    // Do NOT add setLoading(true) here — it would blank the screen on every
    // app-switch.
  }, [refreshKey]);

  const withinCheckinWindow = (shift) => {
    if (!shift) return false;
    // Session W (W4): +02:00 to match the server. The check-in route and the
    // reminder job both build this instant with an explicit SAST offset; this
    // screen did not, so a phone on the wrong timezone got a Check in button
    // the server rejected.
    const start = new Date(`${shift.date}T${shift.time_start || '00:00'}:00+02:00`);
    const hoursUntil = (start - new Date()) / (1000 * 60 * 60);
    return hoursUntil <= 24 && hoursUntil > -2;
  };

  const confirmCheckin = async () => {
    setCheckinLoading(true);
    try {
      await axios.post(`/api/shifts/${nextShift.event_id || nextShift.id}/checkin`);
      setCheckinDone(true);
      setShowCheckinModal(false);
    } catch (e) {
      alert(e.response?.data?.error || 'Could not check in. Please try again.');
    }
    setCheckinLoading(false);
  };

  const openMaps = () => {
    if (!nextShift?.location && !nextShift?.street_address) return;
    const address = [nextShift.street_address, nextShift.location].filter(Boolean).join(', ');
    window.open(`https://maps.google.com?q=${encodeURIComponent(address)}`);
  };

  const isPending = user?.status === 'pending';

  // Session M1 tweak: on a day of a multi-day event, a "per event" flat rate
  // is earned PER DAY — display it as "for the day" (mirrors Schedule.js).
  const payText = (s) => {
    if (!s) return null;
    const t = s.pay_unit === 'per_staff'
      ? (s.applied_rate != null ? `R${Number(s.applied_rate)}/hr` : null)
      : (s.role_pay_rate || s.pay_rate);
    return ((s.series_size || 1) > 1 && s.pay_unit === 'per_event' && t)
      ? String(t).replace('for the event', 'for the day')
      : t;
  };

  return (
    <div className="app-shell">
      <div className="screen">
        <div className="topbar">
          <h2 style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.3px' }}>Next Shift</h2>
        </div>

        {loading ? (
          <div className="loading">Loading...</div>
        ) : isPending ? (
          <div style={{ padding: '32px 20px', textAlign: 'center' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>⏳</div>
            <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>Application Pending</h3>
            <p style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: 24 }}>
              Your profile is under review by the agency. You'll get access to shifts once approved.
            </p>
            <div className="card">
              <div className="card-title" style={{ fontSize: 15, fontWeight: 800, letterSpacing: '-0.2px', marginBottom: 12, color: 'var(--text-primary)' }}>What happens next?</div>
              <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.8 }}>
                1. Agent reviews your profile<br/>
                2. Agent approves your account<br/>
                3. You can then browse &amp; apply for shifts
              </div>
            </div>
          </div>
        ) : nextShift ? (
          <>
            <div className="hero-shift">
              <div className="hero-label">Next Confirmed Shift</div>
              <div className="hero-title">{nextShift.title}</div>
              {(nextShift.series_size || 1) > 1 && (
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 4 }}>
                  Day {nextShift.day_number} of {nextShift.series_size}
                </div>
              )}
              <div className="hero-role">{nextShift.role}</div>
              <div className="hero-meta">
                <span>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                    <rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/>
                  </svg>
                  {formatDate(nextShift.date)}
                </span>
                <span>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                    <circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/>
                  </svg>
                  {nextShift.time_start} – {nextShift.time_end}
                </span>
              </div>
              <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                  <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/><circle cx="12" cy="9" r="2.5"/>
                </svg>
                {nextShift.location}
              </div>
              {payText(nextShift) && (
                <div style={{ marginBottom: 16 }}>
                  <span className="pay-chip">💰 {payText(nextShift)}</span>
                </div>
              )}
              {/* Session M1 tweak: confirmed shift on an event the agency has since
                  unpublished — surface it here too, since this is the screen staff
                  check on the day itself. */}
              {nextShift.published !== 1 && (
                <div style={{ margin: '0 0 16px', padding: '9px 12px', background: 'rgba(217,119,6,0.08)', border: '1px solid rgba(217,119,6,0.3)', borderRadius: 8, fontSize: 12, color: '#a16207', lineHeight: 1.5, textAlign: 'left' }}>
                  ⚠ Your agency has taken this shift off the shift board since you were confirmed. You're still booked — but it's worth checking with them that it's going ahead.
                </div>
              )}
              <button className="btn btn-primary" onClick={openMaps}>
                Get me there →
              </button>
            </div>

            {/* ⚠️ Session AJ BUG FIX: `description` was rendered INSIDE this
                block but was NOT in the condition. An event with a description
                and no dress code and no brief PDF hid the description entirely
                — on the screen staff check on the day itself. `transport` is
                added at the same time: it was displayed on NO staff screen at
                all, despite the agent portal offering the field. */}
            {(nextShift.description || nextShift.transport || nextShift.dress_code || nextShift.brief_url) && (
              <div className="card" style={{ marginTop: 4, borderTop: '2px solid var(--border)', paddingTop: 4 }}>
                <div className="card-title" style={{ fontSize: 15, fontWeight: 800, letterSpacing: '-0.2px', marginBottom: 12, color: 'var(--text-primary)' }}>Event Brief</div>
                <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                  {/* Session AI: same description field as the Schedule detail
                      sheet, so it gets the same clickable links. Leaving one of
                      the two as plain text would be a difference nobody could
                      explain. */}
                  {nextShift.description && <p style={{ marginBottom: 8, overflowWrap: 'anywhere', wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>{linkify(nextShift.description)}</p>}
                  {(nextShift.location || nextShift.street_address) && (
                    <div style={{ marginBottom: 8, padding: '10px 12px', background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 8 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>Full Address</div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', lineHeight: 1.5 }}>
                        {[nextShift.location, nextShift.street_address].filter(Boolean).join(', ')}
                      </div>
                    </div>
                  )}
{nextShift.transport && (
                      <div style={{ marginBottom: 10, padding: '10px 12px', background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 8 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>Transport &amp; Parking</div>
                        <div style={{ fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.5, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', wordBreak: 'break-word' }}>{linkify(nextShift.transport)}</div>
                      </div>
                    )}
                  {nextShift.dress_code && (
                    <div className="flex-row" style={{ marginTop: 4 }}>
                      <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Dress code:</span>
                      <span style={{ fontSize: 13, fontWeight: 600 }}>{nextShift.dress_code}</span>
                    </div>
                  )}
                  {nextShift.brief_url && (
                    <div style={{ marginTop: 10 }}>
                      <button onClick={() => window.open(nextShift.brief_url, '_blank', 'noopener,noreferrer')} style={{ fontSize: 13, fontWeight: 700, color: 'var(--accent)', background: 'rgba(var(--accent-rgb),0.07)', border: '1px solid rgba(var(--accent-rgb),0.25)', borderRadius: 6, padding: '7px 14px', cursor: 'pointer', width: '100%' }}>📄 Read Event Brief</button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Check-in card — only shown within 24 hours of shift */}
            {withinCheckinWindow(nextShift) && (
              <div className="card" style={{ marginTop: 4, borderTop: '2px solid var(--border)', paddingTop: 4 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--text-primary)', marginBottom: 3 }}>Attendance Check-in</div>
                    <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                      {checkinDone ? 'Your attendance has been confirmed.' : 'Confirm you will be attending this shift.'}
                    </div>
                  </div>
                  {checkinDone ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, background: 'rgba(22,163,74,0.1)', border: '1px solid rgba(22,163,74,0.3)' }}>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth={2.5}><path d="M20 6L9 17l-5-5"/></svg>
                      <span style={{ fontSize: 13, fontWeight: 700, color: '#16a34a' }}>Checked in</span>
                    </div>
                  ) : (
                    <button
                      onClick={() => setShowCheckinModal(true)}
                      style={{ padding: '9px 18px', borderRadius: 8, border: 'none', background: 'var(--accent)', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}
                    >
                      Check in
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Check-in confirmation modal */}
            {showCheckinModal && (
              <div onClick={e => { if (e.target === e.currentTarget) setShowCheckinModal(false); }} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 100, padding: '0 0 env(safe-area-inset-bottom)' }}>
                <div style={{ background: 'var(--bg-card)', borderRadius: '16px 16px 0 0', padding: '28px 24px 32px', width: '100%', maxWidth: 430, boxShadow: '0 -4px 30px rgba(0,0,0,0.2)' }}>
                  <div style={{ width: 36, height: 4, background: 'var(--border)', borderRadius: 2, margin: '0 auto 24px' }} />
                  <div style={{ fontWeight: 800, fontSize: 18, color: 'var(--text-primary)', marginBottom: 6 }}>Confirm your attendance</div>
                  <div style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 6, lineHeight: 1.5 }}>{nextShift.title}</div>
                  <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 24 }}>
                    {formatDate(nextShift.date)} · {nextShift.time_start} – {nextShift.time_end}
                  </div>
                  <button
                    onClick={confirmCheckin}
                    disabled={checkinLoading}
                    style={{ width: '100%', padding: '14px', borderRadius: 10, border: 'none', background: 'var(--accent)', color: '#fff', fontSize: 15, fontWeight: 700, cursor: 'pointer', marginBottom: 10 }}
                  >
                    {checkinLoading ? 'Confirming...' : "Yes, I'll be there"}
                  </button>
                  <button
                    onClick={() => setShowCheckinModal(false)}
                    style={{ width: '100%', padding: '13px', borderRadius: 10, border: '1px solid var(--border)', background: 'var(--bg-surface)', color: 'var(--text-secondary)', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div style={{ padding: '32px 20px', textAlign: 'center' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>📅</div>
            <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>No upcoming shifts</h3>
            <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 24 }}>
              Browse available shifts and apply to get started.
            </p>
            <button className="btn btn-primary" onClick={() => navigate('/book')}>
              Browse Shifts
            </button>
          </div>
        )}


      </div>
      <BottomNav />
    </div>
  );
}
